import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InitiativesDB } from '../../utils/initiativesData';
import { mockUser } from '../../utils/mockData';
import { Button } from '../ui';

const CATEGORIES = ['access', 'quality', 'staffing', 'finance', 'other'];
const STATUSES = ['planning', 'in-progress', 'on-hold', 'completed'];

export default function InitiativeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== 'new';
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'access',
    status: 'planning',
    percentComplete: 0,
    startDate: '',
    dueDate: '',
    programManagerName: ''
  });
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) {
      setForm(prev => ({ ...prev, programManagerName: mockUser?.name || '' }));
      setLoading(false);
      return;
    }
    const initiative = InitiativesDB.getInitiative(id);
    if (!initiative) {
      setLoading(false);
      return;
    }
    setForm({
      name: initiative.name || '',
      description: initiative.description || '',
      category: initiative.category || 'access',
      status: initiative.status || 'planning',
      percentComplete: initiative.percentComplete ?? 0,
      startDate: initiative.startDate ? initiative.startDate.split('T')[0] : '',
      dueDate: initiative.dueDate ? initiative.dueDate.split('T')[0] : '',
      programManagerName: initiative.programManagerName || ''
    });
    setLoading(false);
  }, [id, isEdit]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...(isEdit ? { id } : { id: InitiativesDB.generateId('INIT') }),
      ...form,
      programManagerId: mockUser?.id || 'user-001',
      createdBy: mockUser?.id || 'user-001',
      overallHealth: 'green',
      kpiHealth: 'green',
      timelineHealth: 'green'
    };
    InitiativesDB.saveInitiative(payload);
    navigate(`/strategic-initiatives/${payload.id}`);
  };

  const handleCancel = () => {
    if (isEdit) navigate(`/strategic-initiatives/${id}`);
    else navigate('/strategic-initiatives');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <p className="text-text-dark-secondary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Button type="button" variant="default" size="sm" onClick={handleCancel} style={{ marginBottom: 12 }}>← Cancel</Button>
        <h1 className="dashboard-title">{isEdit ? 'Edit Initiative' : 'New Initiative'}</h1>
        <p className="dashboard-subtitle">{isEdit ? 'Update initiative details' : 'Create a new strategic initiative'}</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ maxWidth: 640, padding: 24 }}>
        <div style={{ padding: 0 }}>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Name *</label>
            <input type="text" value={form.name} onChange={e => handleChange('name', e.target.value)} required className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" placeholder="Initiative name" />
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={4} className="w-full rounded-lg border border-nn-border bg-nn-white px-3 py-2 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ fontFamily: 'inherit', resize: 'vertical' }} placeholder="Brief description" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="mb-4">
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Category</label>
              <select value={form.category} onChange={e => handleChange('category', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Status</label>
              <select value={form.status} onChange={e => handleChange('status', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="mb-4">
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => handleChange('startDate', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => handleChange('dueDate', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Program Manager</label>
            <input type="text" value={form.programManagerName} onChange={e => handleChange('programManagerName', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" placeholder="Name" />
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Progress %</label>
            <input type="number" min={0} max={100} value={form.percentComplete} onChange={e => handleChange('percentComplete', Number(e.target.value) || 0)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button type="submit" variant="primary" size="sm">Save</Button>
            <Button type="button" variant="default" size="sm" onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
